import { useState } from 'react';
import { apiCreateProduct, type ProductDetail, type ProductStatus, type Shop } from '~/lib/api/shop';
import { classNames } from '~/utils/classNames';
import { BTN_GHOST, CARD, ErrorBanner, Field, Select, Spinner } from '~/components/workbench/tools/payments/ui';
import { OptionsFields } from './OptionsFields';
import {
  apiErrorMessage,
  parseCount,
  parseMoney,
  parseOptionDrafts,
  parseTags,
  PRODUCT_STATUSES,
  productStatusMeta,
  type OptionDraft,
} from './shop-model';

export function NewProductForm({
  appId,
  shop,
  onDone,
}: {
  appId: string;
  shop: Shop;
  onDone: (created: ProductDetail | null) => void;
}) {
  const [title, setTitle] = useState('');
  const [price, setPrice] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<ProductStatus>('draft');
  const [tags, setTags] = useState('');
  const [options, setOptions] = useState<OptionDraft[]>([]);
  const [stock, setStock] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!title.trim()) {
      setError('Give the product a title.');
      return;
    }

    const minor = parseMoney(price, shop.currency, { allowZero: true });

    if (minor === null) {
      setError(`Enter a price in ${shop.currency}, e.g. 499.`);
      return;
    }

    const parsed = parseOptionDrafts(options);

    if (!parsed.ok) {
      setError(parsed.error);
      return;
    }

    const startingStock = stock.trim() ? parseCount(stock) : null;

    if (stock.trim() && startingStock === null) {
      setError('Starting stock is a whole number, or leave it blank to not track stock.');
      return;
    }

    setSaving(true);

    try {
      const created = await apiCreateProduct(appId, {
        title: title.trim(),
        price: minor,
        description: description.trim() || null,
        status,
        tags: parseTags(tags),
        options: parsed.options,
        ...(startingStock !== null ? { stock: startingStock } : {}),
      });
      onDone(created);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not create this product.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className={classNames(CARD, 'space-y-3 p-4')}>
      <Field label="Title">
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Blue mug" className="ui-input" />
      </Field>

      <div className="flex gap-2">
        <div className="min-w-0 flex-1">
          <Field label={`Price (${shop.currency})`}>
            <input
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              inputMode="decimal"
              placeholder="499"
              className="ui-input-mono"
            />
          </Field>
        </div>
        <div className="w-32">
          <Field label="Status">
            <Select value={status} onChange={(v) => setStatus(v as ProductStatus)} label="Status">
              {PRODUCT_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {productStatusMeta(s).label}
                </option>
              ))}
            </Select>
          </Field>
        </div>
      </div>

      <Field label="Description">
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          placeholder="Stoneware, 350 ml, dishwasher safe."
          className="ui-input resize-y"
        />
      </Field>

      <Field label="Tags" hint="Comma separated. Rule-based collections match on tags.">
        <input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="mugs, gifts" className="ui-input" />
      </Field>

      <OptionsFields drafts={options} onChange={setOptions} productTitle={title} skuPattern={shop.skuPattern} />

      <Field label="Starting stock" hint="Per variant. Leave blank to sell without tracking stock.">
        <input
          value={stock}
          onChange={(e) => setStock(e.target.value)}
          inputMode="numeric"
          placeholder="Untracked"
          className="ui-input-mono !w-32"
        />
      </Field>

      {status !== 'active' && (
        <p className="text-[11px] leading-relaxed text-muted">Draft products stay hidden from the storefront.</p>
      )}

      {error && <ErrorBanner message={error} />}

      <div className="flex items-center gap-2">
        <button type="submit" disabled={saving} className="ui-btn-accent !px-4 !py-2 !text-[12px]">
          {saving && <Spinner />}
          {saving ? 'Creating…' : 'Create product'}
        </button>
        <button type="button" onClick={() => onDone(null)} disabled={saving} className={BTN_GHOST}>
          Cancel
        </button>
      </div>
    </form>
  );
}
